import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc, addDoc, updateDoc, collection, serverTimestamp } from 'firebase/firestore';
import FadeIn from '../../components/FadeIn';
import { ArrowLeft, Save } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { triggerAutoUpdate } from '../../utils/sitemapService';

const GlossaryEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;
    useAuth();

    const [term, setTerm] = useState('');
    const [definition, setDefinition] = useState('');
    const [slug, setSlug] = useState('');
    const [metaTitle, setMetaTitle] = useState('');
    const [metaDescription, setMetaDescription] = useState('');
    const [published, setPublished] = useState(true); // Default to true
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Quill modules configuration
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'align': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link'],
            ['clean']
        ],
    };

    useEffect(() => {
        const fetchTerm = async () => {
            setLoading(true);
            try {
                const docRef = doc(db, 'glossary', id);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setTerm(data.term);
                    setDefinition(data.definition);
                    setSlug(data.slug || '');
                    setMetaTitle(data.metaTitle || '');
                    setMetaDescription(data.metaDescription || '');
                    setPublished(data.published !== undefined ? data.published : true);
                } else {
                    setError('Term not found');
                }
            } catch (err) {
                setError('Error fetching term: ' + err.message);
            } finally {
                setLoading(false);
            }
        };

        if (isEditing) {
            fetchTerm();
        }
    }, [id, isEditing]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Generate slug if empty (mostly for new terms)
            let finalSlug = slug;
            if (!finalSlug) {
                // Remove parenthetical parts for cleaner slug (e.g. "BPO (Business...)" -> "bpo")
                const mainTerm = term.split('(')[0].trim();
                finalSlug = mainTerm.toLowerCase()
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/(^-|-$)+/g, '');
            }

            const termData = {
                term,
                definition,
                slug: finalSlug,
                metaTitle,
                metaDescription,
                published,
                updatedAt: serverTimestamp()
            };

            if (isEditing) {
                await updateDoc(doc(db, 'glossary', id), termData);
            } else {
                termData.createdAt = serverTimestamp();
                await addDoc(collection(db, 'glossary'), termData);
            }

            // Trigger Sitemap Update
            await triggerAutoUpdate();

            navigate('/admin/glossary');
        } catch (err) {
            console.error(err);
            setError('Error saving term. ' + (err.code === 'permission-denied' ? 'Check Firestore Rules.' : err.message));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen py-12 px-4 sm:px-6 lg:px-8" style={{ background: 'var(--bg)' }}>
            <div className="max-w-4xl mx-auto">
                <FadeIn>
                    <div className="flex items-center mb-8 space-x-4">
                        <Link to="/admin/glossary" style={{ color: 'var(--muted)' }} className="hover:text-white transition-colors">
                            <ArrowLeft size={24} />
                        </Link>
                        <h1 className="text-3xl font-bold text-white">
                            {isEditing ? 'Edit Term' : 'Add New Term'}
                        </h1>
                    </div>

                    <div className="rounded-xl shadow-2xl p-8 border" style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
                        {error && <div className="bg-red-900/40 border border-red-500/50 text-red-200 px-4 py-3 rounded mb-6">{error}</div>}

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>Term</label>
                                <input
                                    type="text"
                                    required
                                    value={term}
                                    onChange={(e) => setTerm(e.target.value)}
                                    className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all"
                                    style={{ background: 'var(--surface2)', borderColor: 'var(--border)', color: 'var(--text)' }}
                                    placeholder="e.g. BPO"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>URL Slug (Auto-generated)</label>
                                <input
                                    type="text"
                                    value={slug}
                                    readOnly
                                    className="w-full px-4 py-3 rounded-lg border focus:outline-none cursor-not-allowed font-mono text-sm"
                                    style={{ background: 'var(--surface2)', borderColor: 'var(--border)', color: 'var(--muted)' }}
                                    placeholder="will-be-auto-generated"
                                />
                                <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>This will be used for the URL: /glossary/{slug || '...'}</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>Meta Title (SEO)</label>
                                    <input
                                        type="text"
                                        value={metaTitle}
                                        onChange={(e) => setMetaTitle(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all"
                                        style={{ background: 'var(--surface2)', borderColor: 'var(--border)', color: 'var(--text)' }}
                                        placeholder="e.g. What is BPO? Definition & Examples"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>Meta Description (SEO)</label>
                                    <input
                                        type="text"
                                        value={metaDescription}
                                        onChange={(e) => setMetaDescription(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg border outline-none focus:ring-2 focus:ring-[#A2CB52] transition-all"
                                        style={{ background: 'var(--surface2)', borderColor: 'var(--border)', color: 'var(--text2)' }}
                                        placeholder="Brief summary for search engines..."
                                    />
                                </div>
                            </div>

                            <div className="quill-dark-wrapper">
                                <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text2)' }}>Definition</label>
                                <style>{`
                                    .quill-dark-wrapper .ql-toolbar {
                                        background: var(--surface2) !important;
                                        border-color: var(--border) !important;
                                        border-top-left-radius: 8px;
                                        border-top-right-radius: 8px;
                                    }
                                    .quill-dark-wrapper .ql-container {
                                        background: var(--surface2) !important;
                                        border-color: var(--border) !important;
                                        border-bottom-left-radius: 8px;
                                        border-bottom-right-radius: 8px;
                                        color: var(--text) !important;
                                        font-family: inherit !important;
                                        font-size: 14px !important;
                                        min-height: 250px;
                                    }
                                    .quill-dark-wrapper .ql-stroke {
                                        stroke: var(--muted) !important;
                                    }
                                    .quill-dark-wrapper .ql-fill {
                                        fill: var(--muted) !important;
                                    }
                                    .quill-dark-wrapper .ql-picker {
                                        color: var(--muted) !important;
                                    }
                                    .quill-dark-wrapper .ql-picker-options {
                                        background: var(--surface3) !important;
                                        border-color: var(--border) !important;
                                    }
                                    .quill-dark-wrapper .ql-editor.ql-blank::before {
                                        color: var(--muted) !important;
                                        font-style: normal !important;
                                    }
                                `}</style>
                                <ReactQuill
                                    theme="snow"
                                    value={definition}
                                    onChange={setDefinition}
                                    modules={modules}
                                    className="h-64 mb-12"
                                />
                            </div>

                            <div className="flex items-center space-x-3 pt-8">
                                <input
                                    type="checkbox"
                                    id="published"
                                    checked={published}
                                    onChange={(e) => setPublished(e.target.checked)}
                                    className="h-5 w-5 bg-transparent border-gray-600 rounded text-[#A2CB52] focus:ring-[#A2CB52]"
                                />
                                <label htmlFor="published" className="text-sm font-medium" style={{ color: 'var(--text2)' }}>
                                    Publish immediately
                                </label>
                            </div>

                            <div className="flex justify-end pt-4 border-t" style={{ borderColor: 'var(--border)' }}>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex items-center px-8 py-3 rounded-lg font-bold transition-all shadow-lg disabled:opacity-50 hover:scale-[1.02] active:scale-[0.98]"
                                    style={{ backgroundColor: 'var(--accent)', color: '#000' }}
                                >
                                    <Save size={20} className="mr-2" />
                                    {loading ? 'Saving...' : 'Save Term'}
                                </button>
                            </div>
                        </form>
                    </div>
                </FadeIn>
            </div>
        </div>
    );
};

export default GlossaryEditor;
